// Entity interpolation for every hero that is not the player's own (net/predictedGame.ts predicts that
// one instead). The server only broadcasts at 20 Hz (match.ts's SNAPSHOT_EVERY): rendering the newest
// snapshot directly the instant it arrives makes every other hero visibly step once per ~50ms while the
// player's own glides at 60 Hz — and a skillshot aimed at a stepping target is aimed at where it WAS, not
// where the server currently has it. This buffers the last few received snapshots and samples a pose
// `delayMs` behind the newest data, interpolating continuous fields between the two buffered ticks either
// side of that render instant — the mirror image of predictedGame.ts's "render slightly in the future"
// for the local hero, this renders slightly in the past so there is (almost) always real data on both
// sides to interpolate between.
//
// The render instant is its own continuous clock (advance()), never rounded and never re-anchored in a
// step: it runs at wall-clock rate and is slewed (at most ±10% of real time) toward "the snapshot stream's
// own arrival timeline minus delayMs". That timeline is a filtered fit of each in-order arrival's tick
// against its arrival time, so it already contains the one-way latency — no separate estimate of the
// server's live tick is needed. Derived from a whole-tick, pong-anchored clock instead, the render instant
// stood still or jumped whole ticks: at 144 Hz 58% of frames drew another hero not moving at all, and
// every pong moved everyone by up to ±5 ticks (≈0.47 m) at 200 ms/50 ms.
//
// Fog honesty (the same anti-cheat line as everywhere else in this project): a hero's visible/alive flag
// is read from the SAME delayed timeline as its position, never from whichever snapshot most recently
// arrived — so becoming visible snaps straight to the position the server gave for that tick (it is never
// interpolated against a stale pre-reveal frame), and going invisible or dying simply holds the last
// honest frame instead of continuing to glide along a heading the server never confirmed.
//
// Adaptive delay: too small and the render instant regularly outruns the buffer (nothing to interpolate
// toward); too large and everyone else's actions read stale for no reason beyond what the eye needs.
// Derived from the measured 20 Hz send interval (the smallest positive tick gap ever seen between two
// accepted snapshots — packet loss only ever makes a gap a clean multiple of it, never smaller) plus a
// windowed high percentile of arrival-timing jitter (how far each arrival lands from the nearest whole
// multiple of that interval — this stays ~0 through ordinary loss, since a skipped snapshot's gap is still
// a clean multiple, and only grows for genuine irregularity). A percentile over a bounded window, not an
// EWMA: a single freak sample ages out completely once JITTER_WINDOW samples have passed, rather than
// decaying out slowly over many — an unbounded-tail EWMA is exactly what let one bad measurement keep
// inflating the reported delay long after the connection (or the tab) had recovered. Hysteresis on the
// FINAL delay still prevents oscillation: it moves toward the target fast when the target is HIGHER (a
// felt stutter costs more than a few extra ms of lag) and slowly when it is lower (only easing back down
// once the connection has clearly calmed).
//
// A stall guard sits in front of all of this: `ingest()`'s `nowMs` is stamped by the caller at the exact
// moment its own snapshot handler runs off the raw WebSocket `message` event (remoteGame.ts's
// applySnapshot default parameter) — as early as page JS can observe arrival, not deferred through the
// rendered-frame loop. That still isn't proof against the TAB itself having stopped running for a while
// (backgrounded, thrown to sleep, a long GC pause): the browser keeps delivering queued messages once the
// main thread wakes up, so several genuinely-on-time snapshots can end up processed back-to-back in one
// burst, reading as a huge, false "gap" for the one BEFORE the stall and a suspicious near-zero gap for
// the ones IN the burst. A gap far larger than any plausible ordinary irregularity (STALL_GAP_MS) is
// discarded outright — this was measured causing an 85ms reported delay on an idle 2ms-RTT localhost
// connection before this guard existed. The recovery burst right after it is discarded too, but only for a
// short COOLDOWN of samples following a detected stall, not by a blanket "this gap looks too short" rule on
// every arrival: real, ordinary jitter routinely bunches arrivals close together with no stall involved at
// all (measured firing on 6-9% of arrivals at 120-200ms under a virtual clock, where a local stall is
// physically impossible) — exactly the genuine irregularity the jitter margin exists to widen for, which a
// gap-size-alone rule cannot tell apart from a real stall's aftermath.
import { STEP_DT } from '../../core/game.js';
import type { HeroId } from '../../core/events.js';
import type { HeroSnapshot, MonsterSnapshot, SnapshotMessage } from '../../shared/protocol.js';

const TICK_MS = STEP_DT * 1000;
const MAX_FRAMES = 40;          // ~2s of history at 20Hz — comfortably more than MAX_DELAY_MS ever needs
const MIN_DELAY_MS = 50;        // at least ~one send interval, or there's rarely a second bracket point
const MAX_DELAY_MS = 200;       // stale-feeling beyond this, and the server's own fairness cap (config.
                                 // ts's maxRewindMs) is the same order anyway — see protocol.ts's comment
const DEFAULT_DELAY_MS = 100;   // bootstrap, before two snapshots exist to measure anything from — matches
                                 // config.ts's old constant so day-one feel doesn't regress
const RISE_ALPHA = 0.5;         // fast: a felt stutter costs more than a few extra ms of lag
const FALL_ALPHA = 0.05;        // slow: only ease down once the connection has clearly calmed
const JITTER_WINDOW = 20;       // ~1s of samples at 20Hz — long enough to judge "how irregular", short
                                 // enough that a one-off outlier is fully gone within a second, not decaying
                                 // out of an EWMA's tail for much longer
const JITTER_PERCENTILE = 0.9;  // a margin should cover most of what actually happens, not just the mean
const MAX_EXTRAPOLATE_MS = 100; // "a very short gap, if at all" — well under one prediction-worthy step
const STALL_GAP_MS = 500;       // ~10x the 20Hz send interval — a gap this large between two arrivals is
                                 // far more plausibly OUR OWN processing having paused than the network
const STALL_COOLDOWN_SAMPLES = 10;  // arrivals right after a detected stall that stay excluded too — long
                                     // enough to drain a realistic queued-message burst, short enough that
                                     // ordinary jitter starts counting again well within one JITTER_WINDOW
const ARRIVAL_GAIN = 0.05;      // per arrival: the arrival timeline follows a real latency change within ~1s
                                 // while one late or early snapshot moves it by a twentieth of its error
const ARRIVAL_RESET_TICKS = 30; // an arrival this far off the fitted timeline is a discontinuity, taken as is
const CLOCK_SNAP_TICKS = 15;    // the render clock jumps only this far off its target (a tab stall, the start)
const CLOCK_SLEW_GAIN = 1.8;    // 1/s: fraction of the remaining drift closed per second (remoteGame's steerClock)
const CLOCK_SLEW_MAX = 0.1;     // at most ±10% of real time — never a visible speed-up or a stop

type Frame = { tick: number; heroes: readonly HeroSnapshot[]; monsters: readonly MonsterSnapshot[] };

type VisibleHero = Extract<HeroSnapshot, { visible: true }>;

/** `from` is the buffered entry every discrete field (alive, hp, statuses) must be read from, so they change
    on the same delayed timeline as the position they go with; `sinceTicks` is how far the render instant
    sits past that entry's own tick, for turning its seconds-left fields into deadlines. */
export type InterpolatedPose =
  | { visible: false }
  | { visible: true; pos: { x: number; z: number }; yaw: number; lift: number; state: string; moving: boolean; from: VisibleHero; sinceTicks: number };

/** A monster's sample carries `stateT` too (advanced by however far the render instant sits past the
    frame it was read from), so a clip can be scrubbed to the right point of a windup, and `alive`, so a
    death/respawn lands on the same delayed timeline as the position it goes with — a respawned monster is
    never drawn alive at its death spot while the den frame is still in the future. */
export type InterpolatedMonsterPose =
  | { visible: false }
  | { visible: true; alive: boolean; pos: { x: number; z: number }; yaw: number; state: string; stateT: number; moving: boolean };

export type EntityInterpolator = {
  /** Buffers one received snapshot and folds its arrival timing into the adaptive delay estimate. */
  ingest(msg: SnapshotMessage, nowMs: number): void;
  /** Advances the render clock by `dt` seconds of wall time and returns it (a fractional tick), or null
      before any snapshot has arrived — see this module's header. */
  advance(dt: number, nowMs: number): number | null;
  /** The pose `heroId` should render right now, `renderTick` being "now" on the delayed timeline (the
      caller's job — see remoteGame.ts's interpolateOthers). `null` only before any snapshot has ever been
      buffered for this hero at all. */
  sample(heroId: HeroId, renderTick: number): InterpolatedPose | null;
  /** Same timeline and rules as `sample`, for a monster id (index into LAYOUT.bosses). */
  sampleMonster(monsterId: number, renderTick: number): InterpolatedMonsterPose | null;
  readonly delayMs: number;
  /** Arrivals discarded from the jitter measurement as a likely local stall (see STALL_GAP_MS) — read by
      the `?net` overlay so a contaminated-looking delay is visibly explained rather than silently hidden
      behind a smoothed number that looks plausible in isolation. */
  readonly stallCount: number;
};

/** The `p`-th percentile (0..1) of `values`, nearest-rank — good enough for a ~20-sample window where
    interpolating between ranks buys no real precision. */
function percentile(values: readonly number[], p: number): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const idx = Math.min(sorted.length - 1, Math.floor(p * sorted.length));
  return sorted[idx]!;
}

const angDiff = (a: number, b: number) => Math.atan2(Math.sin(b - a), Math.cos(b - a));

function poseFrom(hs: HeroSnapshot | undefined, sinceTicks: number): InterpolatedPose | null {
  if (!hs) return null;
  if (!hs.visible) return { visible: false };
  return { visible: true, pos: { x: hs.pos.x, z: hs.pos.z }, yaw: hs.yaw, lift: hs.lift, state: hs.state, moving: hs.moving, from: hs, sinceTicks: Math.max(0, sinceTicks) };
}

function heroOf(frame: Frame, heroId: HeroId): HeroSnapshot | undefined {
  return frame.heroes[heroId];
}

/* Wire entry plus the ticks elapsed since its frame, for advancing `stateT`. */
function monsterPoseFrom(ms: MonsterSnapshot | undefined, sinceTicks: number): InterpolatedMonsterPose | null {
  if (!ms) return null;
  if (!ms.visible) return { visible: false };
  return {
    visible: true, alive: ms.alive, pos: { x: ms.pos.x, z: ms.pos.z }, yaw: ms.yaw, state: ms.state,
    stateT: ms.stateT + Math.max(0, sinceTicks) * STEP_DT, moving: ms.moving,
  };
}

export function createEntityInterpolator(): EntityInterpolator {
  const frames: Frame[] = [];
  let lastArrivalTick: number | null = null;
  let lastArrivalCallMs: number | null = null;
  let smallestTickDelta = Infinity;
  const deviations: number[] = [];  // bounded window — see JITTER_WINDOW
  let delayMs = DEFAULT_DELAY_MS;
  let stallCount = 0;
  let stallCooldown = 0;
  /** The arrival timeline: the tick the stream is at as of `arrivalAtMs` — see this module's header. */
  let arrivalTick: number | null = null, arrivalAtMs = 0, lastLineTick = -Infinity;
  let renderTick: number | null = null;

  function recomputeDelay() {
    if (smallestTickDelta === Infinity) return;  // nothing measured yet — keep the bootstrap default
    const sendIntervalMs = smallestTickDelta * TICK_MS;
    const jitterMs = percentile(deviations, JITTER_PERCENTILE);
    const needed = Math.min(MAX_DELAY_MS, Math.max(MIN_DELAY_MS, sendIntervalMs + jitterMs));
    const alpha = needed > delayMs ? RISE_ALPHA : FALL_ALPHA;
    delayMs = Math.min(MAX_DELAY_MS, Math.max(MIN_DELAY_MS, delayMs + alpha * (needed - delayMs)));
  }

  /** The buffered frames either side of `renderTick` (`a` at or before, `b` after); never both undefined
      while any frame exists. */
  function bracket(renderTick: number): { a: Frame | undefined; b: Frame | undefined } {
    let a: Frame | undefined, b: Frame | undefined;
    for (const f of frames) {
      if (f.tick <= renderTick) a = f;
      else { b = f; break; }
    }
    return { a, b };
  }

  /** Beyond the newest buffered tick: hold unless the hero was alive, visible and actively walking as of
      that last frame, in which case its last known velocity (from the two most recent frames) carries it
      forward for a short, capped overshoot — "extrapolate only for a very short gap, if at all", and never
      through a state this frame doesn't already show (a dead or stationary hero is never extrapolated). */
  function extrapolate(a: Frame, heroId: HeroId, renderTick: number): InterpolatedPose | null {
    const ha = heroOf(a, heroId);
    if (!ha) return null;
    const since = renderTick - a.tick;
    if (!ha.visible || !ha.alive || !ha.moving) return poseFrom(ha, since);
    const idx = frames.indexOf(a);
    const prev = idx > 0 ? frames[idx - 1] : undefined;
    const hprev = prev && heroOf(prev, heroId);
    if (!prev || !hprev || !hprev.visible || prev.tick === a.tick) return poseFrom(ha, since);
    const overshootMs = Math.min(MAX_EXTRAPOLATE_MS, since * TICK_MS);
    const dtMs = (a.tick - prev.tick) * TICK_MS;
    const vx = (ha.pos.x - hprev.pos.x) / dtMs, vz = (ha.pos.z - hprev.pos.z) / dtMs;
    return { visible: true, pos: { x: ha.pos.x + vx * overshootMs, z: ha.pos.z + vz * overshootMs }, yaw: ha.yaw, lift: ha.lift, state: ha.state, moving: ha.moving, from: ha, sinceTicks: since };
  }

  return {
    ingest(msg, nowMs) {
      // Insert sorted, deduped by tick — DebugTransport-style reordering (and, in principle, any unordered
      // transport) can deliver an older tick after a newer one already landed; the buffer still wants it
      // to fill the gap it left, even though the arrival-timing stats below only ever look forward.
      let i = frames.length;
      while (i > 0 && frames[i - 1]!.tick > msg.tick) i--;
      // `?? []`: a snapshot from a server predating monsters must not crash sampleMonster().
      if (frames[i]?.tick !== msg.tick) frames.splice(i, 0, { tick: msg.tick, heroes: msg.heroes, monsters: msg.monsters ?? [] });
      while (frames.length > MAX_FRAMES) frames.shift();

      if (lastArrivalTick !== null && msg.tick > lastArrivalTick) {
        smallestTickDelta = Math.min(smallestTickDelta, msg.tick - lastArrivalTick);
      }
      let trusted = true;
      if (lastArrivalCallMs !== null && smallestTickDelta < Infinity) {
        const actualGapMs = nowMs - lastArrivalCallMs;
        const sendIntervalMs = smallestTickDelta * TICK_MS;
        if (actualGapMs > STALL_GAP_MS) {
          stallCount++;
          stallCooldown = STALL_COOLDOWN_SAMPLES;  // the recovery burst right after this is just as untrustworthy
          trusted = false;
        } else if (stallCooldown > 0) {
          stallCooldown--;
          stallCount++;
          trusted = false;
        } else {
          // Snap the observed gap to the nearest whole multiple of the send interval before comparing: a
          // lost packet makes the next gap a clean 2x/3x/... with zero deviation, exactly as it should —
          // only a gap that doesn't land near any multiple is genuine jitter.
          const nearestMultiple = Math.max(1, Math.round(actualGapMs / sendIntervalMs)) * sendIntervalMs;
          deviations.push(Math.abs(actualGapMs - nearestMultiple));
          if (deviations.length > JITTER_WINDOW) deviations.shift();
        }
      }
      lastArrivalCallMs = nowMs;
      if (lastArrivalTick === null || msg.tick > lastArrivalTick) lastArrivalTick = msg.tick;
      recomputeDelay();
      // Countdown snapshots all say tick 0; on the timeline they stand where the server's clock really is
      // then (pong.tick's −ticks left), so it runs on through the start of play without a jump.
      const lineTick = msg.countdown ? -msg.countdown / STEP_DT : msg.tick;
      // Only in-order arrivals the stall guard trusts shape the arrival timeline: a reordered one is older
      // than what already landed, and a stalled burst says when this tab woke up, not when data arrived.
      if (lineTick <= lastLineTick || (!trusted && arrivalTick !== null)) return;
      lastLineTick = lineTick;
      const current = arrivalTick === null ? lineTick : arrivalTick + (nowMs - arrivalAtMs) / TICK_MS;
      const off = lineTick - current;
      arrivalTick = Math.abs(off) > ARRIVAL_RESET_TICKS ? lineTick : current + ARRIVAL_GAIN * off;
      arrivalAtMs = nowMs;
    },

    advance(dt, nowMs) {
      if (arrivalTick === null) return null;
      const target = arrivalTick + (nowMs - arrivalAtMs) / TICK_MS - delayMs / TICK_MS;
      const step = Math.min(dt, 0.25) / STEP_DT;
      if (renderTick === null || Math.abs(target - (renderTick + step)) > CLOCK_SNAP_TICKS) return (renderTick = target);
      const drift = target - (renderTick + step);
      renderTick += step + Math.max(-CLOCK_SLEW_MAX * step, Math.min(CLOCK_SLEW_MAX * step, drift * CLOCK_SLEW_GAIN * Math.min(dt, 0.25)));
      return renderTick;
    },

    sample(heroId, renderTick) {
      if (frames.length === 0) return null;
      const { a, b } = bracket(renderTick);
      if (!a) return poseFrom(heroOf(b!, heroId), 0);         // before the oldest data — best we have
      if (!b) return extrapolate(a, heroId, renderTick);       // beyond the newest — hold or extrapolate
      const ha = heroOf(a, heroId), hb = heroOf(b, heroId);
      const since = renderTick - a.tick;
      if (!ha || !hb) return ha ? poseFrom(ha, since) : poseFrom(hb, 0);
      if (ha.visible !== hb.visible || (ha.visible && hb.visible && ha.alive !== hb.alive)) {
        return poseFrom(ha, since);  // a fog or death/respawn discontinuity between the two — hold, never glide
      }
      if (!ha.visible || !hb.visible) return { visible: false };
      const t = since / (b.tick - a.tick);
      return {
        visible: true,
        pos: { x: ha.pos.x + (hb.pos.x - ha.pos.x) * t, z: ha.pos.z + (hb.pos.z - ha.pos.z) * t },
        yaw: ha.yaw + angDiff(ha.yaw, hb.yaw) * t,
        lift: ha.lift + (hb.lift - ha.lift) * t,
        state: ha.state, moving: ha.moving, from: ha, sinceTicks: since,
      };
    },

    sampleMonster(monsterId, renderTick) {
      if (frames.length === 0) return null;
      const { a, b } = bracket(renderTick);
      if (!a) return monsterPoseFrom(b!.monsters[monsterId], 0);
      const ma = a.monsters[monsterId];
      if (!b) {
        // Hold or extrapolate exactly like a hero: only an alive, visible, walking monster, capped.
        if (!ma || !ma.visible || !ma.alive || !ma.moving) return monsterPoseFrom(ma, renderTick - a.tick);
        const prev = frames[frames.indexOf(a) - 1], mp = prev?.monsters[monsterId];
        const pose = monsterPoseFrom(ma, renderTick - a.tick);
        if (!prev || !mp || !mp.visible || prev.tick === a.tick || !pose || !pose.visible) return pose;
        const overshootMs = Math.min(MAX_EXTRAPOLATE_MS, (renderTick - a.tick) * TICK_MS);
        const dtMs = (a.tick - prev.tick) * TICK_MS;
        pose.pos = { x: ma.pos.x + (ma.pos.x - mp.pos.x) / dtMs * overshootMs, z: ma.pos.z + (ma.pos.z - mp.pos.z) / dtMs * overshootMs };
        return pose;
      }
      const mb = b.monsters[monsterId];
      if (!ma || !mb) return mb ? monsterPoseFrom(mb, 0) : monsterPoseFrom(ma, renderTick - a.tick);
      if (ma.visible !== mb.visible || (ma.visible && mb.visible && ma.alive !== mb.alive)) return monsterPoseFrom(ma, renderTick - a.tick);
      if (!ma.visible || !mb.visible) return { visible: false };
      const t = (renderTick - a.tick) / (b.tick - a.tick);
      return {
        visible: true, alive: ma.alive,
        pos: { x: ma.pos.x + (mb.pos.x - ma.pos.x) * t, z: ma.pos.z + (mb.pos.z - ma.pos.z) * t },
        yaw: ma.yaw + angDiff(ma.yaw, mb.yaw) * t,
        state: ma.state, stateT: ma.stateT + (renderTick - a.tick) * STEP_DT, moving: ma.moving,
      };
    },

    get delayMs() { return delayMs; },
    get stallCount() { return stallCount; },
  };
}
